import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import {
  FormGroup,
  ControlContainer,
  FormGroupDirective,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { MbbProduct } from '../../../models/mbb-product.model';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';
import { FormValidationService } from '@app/shared/services/form-validation.service';

@Component({
  selector: 'app-mbb-user-details',
  templateUrl: './mbb-user-details.component.html',
  styleUrls: ['./mbb-user-details.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [FormsModule, ReactiveFormsModule, IceInputFieldComponent],
})
export class MbbUserDetailsComponent implements OnInit, OnChanges, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);

  getRouterNameErrorText = FormValidationService.getRouterNameErrorText;

  @Input() mbbProduct: MbbProduct;
  @Output() mbbUserDetailsFormChange: EventEmitter<string> = new EventEmitter();
  @Output() isInvalid = new EventEmitter();

  form: FormGroup;

  subscriptions$: Subscription[] = [];

  static buildMbbUserDetailsForm(routerName: string): FormGroup {
    return new FormGroup({
      routerName: new FormControl(routerName, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
    });
  }

  get routerNameField() {
    return this.form.controls.routerName;
  }

  ngOnInit() {
    this.form = this.buildForm(this.mbbProduct);
    this.form.controls.routerName.markAsTouched();

    this.subscriptions$.push(
      this.form.controls.routerName.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          tap(updateValue => this.mbbUserDetailsFormChange.emit(updateValue)),
          tap(() => this.isInvalid.emit(this.form.invalid))
        )
        .subscribe()
    );

    this.isInvalid.emit(this.form.invalid);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mbbProduct) {
      const oldValue = changes.mbbProduct.previousValue;
      const newValue = changes.mbbProduct.currentValue;

      if (oldValue && newValue) {
        const hasRouterChanged = oldValue.mbbRouter?.name !== newValue.mbbRouter?.name;

        if (hasRouterChanged) {
          const hasSameRouterNameAsPreviously = oldValue.mbbRouter.name === this.form.controls.routerName.value;

          if (hasSameRouterNameAsPreviously) {
            this.form.controls.routerName.setValue(newValue.mbbRouter.name);
            this.isInvalid.emit(this.form.invalid);
          }

          const hasRouterName = this.form.controls.routerName.value;
          if (!hasRouterName) {
            this.form.controls.routerName.setValue(newValue.mbbRouter.name);
            this.form.controls.routerName.markAsTouched();
            this.isInvalid.emit(this.form.invalid);
          }
        }
      }
    }
  }

  private buildForm(mbbProduct: MbbProduct): FormGroup {
    this.formGroupDirective.form.addControl(
      'mbbUserDetails',
      MbbUserDetailsComponent.buildMbbUserDetailsForm(mbbProduct.routerName)
    );

    return this.formGroupDirective.form.get('mbbUserDetails') as FormGroup;
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
